<template>
  <div>
    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Welcome back{{ displayName ? ', ' + displayName : '' }}</h5>
        <p class="page-sub">Here's a snapshot of your HR at a glance</p>
      </div>
      <div class="header-date">{{ todayLabel }}</div>
    </div>

    <!-- No profile banner -->
    <div v-if="!employeeId" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Contact your HR administrator to link your account to an employee record.</p>
    </div>

    <!-- Dashboard grid -->
    <template v-else>
      <!-- Skeleton -->
      <div v-if="loading">
        <div class="stat-row">
          <div v-for="n in 4" :key="n" class="stat-card sk-stat"></div>
        </div>
        <div class="dash-grid">
          <div v-for="n in 6" :key="n" class="widget sk-widget">
            <div class="sk-title"></div>
            <div class="sk-body"></div>
          </div>
        </div>
      </div>

      <template v-else>

      <!-- ── 4 Stat cards ───────────────────────────────────────── -->
      <div class="stat-row">
        <div class="stat-card">
          <div class="stat-icon-wrap icon--purple"><font-awesome-icon icon="calendar-times" /></div>
          <div class="stat-body">
            <p class="stat-count">{{ counts.leave }}</p>
            <p class="stat-label">Total Leave Requests</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap icon--orange"><font-awesome-icon icon="hand-holding-usd" /></div>
          <div class="stat-body">
            <p class="stat-count">{{ counts.loans }}</p>
            <p class="stat-label">Total Loans</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap icon--green"><font-awesome-icon icon="money-bill-wave" /></div>
          <div class="stat-body">
            <p class="stat-count">{{ counts.payslips }}</p>
            <p class="stat-label">Total Payslips</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap icon--blue"><font-awesome-icon icon="clock" /></div>
          <div class="stat-body">
            <p class="stat-count">{{ counts.attendance }}</p>
            <p class="stat-label">Attendance Records</p>
          </div>
        </div>
      </div>

      <div class="dash-grid">

        <!-- ── Leave Balances ──────────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--blue"><font-awesome-icon icon="umbrella-beach" /></div>
            <div>
              <p class="widget-title">Leave Balances</p>
              <p class="widget-sub">{{ currentYear }}</p>
            </div>
            <router-link :to="$url('/hr/leave?context=self-service')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!leaveBalances.length" class="widget-empty">No leave types configured</div>
          <div v-else class="leave-balance-list">
            <div v-for="b in leaveBalances.slice(0, 4)" :key="b.leave_type" class="leave-balance-row">
              <span class="lb-name">{{ b.leave_type }}</span>
              <div class="lb-bar-wrap">
                <div class="lb-bar" :style="{ width: barWidth(b) + '%' }"></div>
              </div>
              <span class="lb-count">{{ b.available }} / {{ b.entitled }} days</span>
            </div>
          </div>
        </div>

        <!-- ── Recent Leave Requests ──────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--purple"><font-awesome-icon icon="calendar-times" /></div>
            <div>
              <p class="widget-title">Recent Leave</p>
              <p class="widget-sub">Your last requests</p>
            </div>
            <router-link :to="$url('/hr/leave?context=self-service')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!recentLeave.length" class="widget-empty">No leave requests found</div>
          <div v-else class="leave-request-list">
            <div v-for="r in recentLeave" :key="r.id" class="leave-req-row">
              <div class="lr-info">
                <p class="lr-type">{{ r.leave_type || 'Leave' }}</p>
                <p class="lr-dates">{{ fmtDate(r.from_date) }} – {{ fmtDate(r.to_date) }} &nbsp;({{ r.days }}d)</p>
              </div>
              <span class="status-pill" :class="statusClass(r.status)">{{ r.status }}</span>
            </div>
          </div>
        </div>

        <!-- ── Upcoming Shifts ────────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--teal"><font-awesome-icon icon="calendar-check" /></div>
            <div>
              <p class="widget-title">Upcoming Shifts</p>
              <p class="widget-sub">Active &amp; next 14 days</p>
            </div>
            <router-link :to="$url('/hr/my-shifts')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!upcomingShifts.length" class="widget-empty">No shifts scheduled</div>
          <div v-else class="shift-list">
            <div v-for="s in upcomingShifts" :key="s.date + s.shift_name" class="shift-row">
              <div class="shift-date-col">
                <template v-if="s.is_ongoing">
                  <p class="shift-dow">Ongoing</p>
                  <p class="shift-date">from {{ fmtDate(s.date) }}</p>
                </template>
                <template v-else>
                  <p class="shift-dow">{{ dayOfWeek(s.date) }}</p>
                  <p class="shift-date">{{ fmtDate(s.date) }}</p>
                </template>
              </div>
              <div class="shift-info">
                <p class="shift-name">{{ s.shift_name }}</p>
                <p class="shift-time">{{ fmtTime(s.start_time) }} – {{ fmtTime(s.end_time) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Latest Payslip ─────────────────────────────────── -->
        <div class="widget widget--payslip">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--green"><font-awesome-icon icon="money-bill-wave" /></div>
            <div>
              <p class="widget-title">Latest Payslip</p>
              <p class="widget-sub">{{ latestPayslip ? latestPayslip.period_name : '—' }}</p>
            </div>
            <router-link :to="$url('/hr/my-payslips')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!latestPayslip" class="widget-empty">No payslips yet</div>
          <div v-else class="payslip-body">
            <div class="pay-row">
              <span class="pay-label">Gross Pay</span>
              <span class="pay-value">{{ fmtCurrency(latestPayslip.gross_pay) }}</span>
            </div>
            <div class="pay-row pay-row--net">
              <span class="pay-label">Net Pay</span>
              <span class="pay-value pay-value--net">{{ fmtCurrency(latestPayslip.net_pay) }}</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Period</span>
              <span class="pay-value pay-value--sm">{{ fmtDate(latestPayslip.start_date) }} – {{ fmtDate(latestPayslip.end_date) }}</span>
            </div>
          </div>
        </div>

        <!-- ── Attendance Summary ──────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--orange"><font-awesome-icon icon="clock" /></div>
            <div>
              <p class="widget-title">Attendance</p>
              <p class="widget-sub">This month</p>
            </div>
            <router-link :to="$url('/hr/my-attendance')" class="widget-link">View all</router-link>
          </div>
          <div class="attendance-grid">
            <div class="att-card att-card--present">
              <p class="att-count">{{ attendanceSummary.present || 0 }}</p>
              <p class="att-label">Present</p>
            </div>
            <div class="att-card att-card--absent">
              <p class="att-count">{{ attendanceSummary.absent || 0 }}</p>
              <p class="att-label">Absent</p>
            </div>
            <div class="att-card att-card--late">
              <p class="att-count">{{ attendanceSummary.late || 0 }}</p>
              <p class="att-label">Late</p>
            </div>
            <div class="att-card att-card--leave">
              <p class="att-count">{{ attendanceSummary.on_leave || 0 }}</p>
              <p class="att-label">On Leave</p>
            </div>
          </div>
        </div>

        <!-- ── Notifications ──────────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--red"><font-awesome-icon icon="bell" /></div>
            <div>
              <p class="widget-title">Notifications</p>
              <p class="widget-sub">
                <span v-if="unreadNotifications" class="unread-badge">{{ unreadNotifications }} unread</span>
                <span v-else>All caught up</span>
              </p>
            </div>
            <router-link :to="$url('/hr/my-notifications')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!recentNotifications.length" class="widget-empty">No notifications yet</div>
          <div v-else class="notif-mini-list">
            <div
              v-for="n in recentNotifications"
              :key="n.id"
              class="notif-mini-row"
              :class="{ 'notif-mini-row--unread': !n.read_at }"
            >
              <div class="notif-mini-icon" :class="notifIconClass(n.data?.type)">
                <font-awesome-icon :icon="notifIcon(n.data?.type)" />
              </div>
              <div class="notif-mini-body">
                <p class="notif-mini-msg">{{ n.data?.message || 'Notification' }}</p>
                <p class="notif-mini-time">{{ timeAgo(n.created_at) }}</p>
              </div>
              <div v-if="!n.read_at" class="notif-dot"></div>
            </div>
          </div>
        </div>

        <!-- ── Upcoming Birthdays ─────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--pink"><font-awesome-icon icon="birthday-cake" /></div>
            <div>
              <p class="widget-title">Upcoming Birthdays</p>
              <p class="widget-sub">Next 30 days</p>
            </div>
          </div>
          <div v-if="!upcomingBirthdays.length" class="widget-empty">No birthdays in the next 30 days</div>
          <div v-else class="birthday-list">
            <div v-for="b in upcomingBirthdays" :key="b.employee_id" class="birthday-row">
              <div class="bday-avatar">{{ initials(b.name) }}</div>
              <div class="bday-info">
                <p class="bday-name">{{ b.name }}</p>
                <p class="bday-date">{{ fmtDate(b.birthday_date) }}</p>
              </div>
              <span v-if="b.is_today" class="bday-today-pill">Today 🎂</span>
              <span v-else class="bday-days">in {{ b.days_away }}d</span>
            </div>
          </div>
        </div>

        <!-- ── Recent Documents ──────────────────────────────── -->
        <div class="widget">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--indigo"><font-awesome-icon icon="file-alt" /></div>
            <div>
              <p class="widget-title">Recent Documents</p>
              <p class="widget-sub">Published for you</p>
            </div>
            <router-link :to="$url('/hr/my-documents')" class="widget-link">View all</router-link>
          </div>
          <div v-if="!recentDocuments.length" class="widget-empty">No documents published yet</div>
          <div v-else class="doc-mini-list">
            <div v-for="d in recentDocuments" :key="d.id" class="doc-mini-row" :class="{ 'doc-mini-row--unread': !d.is_read }">
              <font-awesome-icon :icon="docMiniIcon(d.file_type)" class="doc-mini-icon" />
              <div class="doc-mini-body">
                <p class="doc-mini-title">{{ d.title }}</p>
                <p class="doc-mini-meta">{{ d.category || 'Uncategorised' }} &nbsp;·&nbsp; {{ fmtDate(d.published_at) }}</p>
              </div>
              <div class="doc-mini-badges">
                <span v-if="!d.is_read" class="mini-badge mini-badge--blue">New</span>
                <span v-if="d.requires_acknowledgement && !d.is_acknowledged" class="mini-badge mini-badge--amber">Ack</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Company Announcements ──────────────────────────── -->
        <div class="widget widget--full">
          <div class="widget-head">
            <div class="widget-icon-wrap icon--indigo"><font-awesome-icon icon="bullhorn" /></div>
            <div style="flex:1;">
              <p class="widget-title">Announcements</p>
              <p class="widget-sub">Company news &amp; updates</p>
            </div>
            <router-link :to="$url('/hr/my-announcements')" class="widget-view-all">View All</router-link>
          </div>
          <div v-if="!announcements.length" class="widget-empty">No announcements at the moment</div>
          <div v-else class="announcement-list">
            <div v-for="a in announcements" :key="a.id" class="announcement-item">
              <div class="ann-header">
                <p class="ann-title">{{ a.title }}</p>
                <p class="ann-date">{{ fmtDate(a.publish_at || a.created_at) }}</p>
              </div>
              <p class="ann-body">{{ stripHtml(a.body) }}</p>
              <div class="ann-footer">
                <span class="ann-stat"><font-awesome-icon icon="heart" class="stat-icon stat-icon--red" /> {{ a.likes_count || 0 }}</span>
                <span class="ann-stat"><font-awesome-icon icon="comment" class="stat-icon" /> {{ a.comments_count || 0 }}</span>
                <router-link :to="$url('/hr/my-announcements')" class="ann-interact-link">Like &amp; Comment</router-link>
              </div>
            </div>
          </div>
        </div>

      </div>

      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

const NOTIF_META = {
  leave_request:     { icon: 'umbrella-beach', cls: 'icon--blue' },
  leave_approved:    { icon: 'check-circle',   cls: 'icon--green' },
  leave_rejected:    { icon: 'times-circle',   cls: 'icon--red' },
  missing_clock_out: { icon: 'clock',           cls: 'icon--orange' },
  payroll_processed: { icon: 'money-bill-wave', cls: 'icon--green' },
  shift_assigned:    { icon: 'calendar-check',  cls: 'icon--teal' },
  announcement:      { icon: 'bullhorn',         cls: 'icon--indigo' },
  new_document:      { icon: 'file-alt',         cls: 'icon--indigo' },
  default:           { icon: 'bell',            cls: 'icon--gray' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)     return 'Just now';
  if (diff < 3600)   return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)  return Math.floor(diff / 3600) + 'h ago';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default {
  name: 'MyDashboardPage',

  data() {
    return {
      loading: false,
      counts: { leave: 0, loans: 0, payslips: 0, attendance: 0 },
      leaveBalances: [],
      recentLeave: [],
      upcomingShifts: [],
      latestPayslip: null,
      attendanceSummary: { present: 0, absent: 0, late: 0, on_leave: 0 },
      unreadNotifications: 0,
      recentNotifications: [],
      upcomingBirthdays: [],
      announcements: [],
      recentDocuments: [],
    };
  },

  computed: {
    employeeId() {
      return this.$store?.state?.getLoggedInUser?.employee_id || null;
    },
    displayName() {
      const u = this.$store?.state?.getLoggedInUser;
      return u?.first_name || u?.name?.split(' ')[0] || '';
    },
    currentYear() {
      return new Date().getFullYear();
    },
    todayLabel() {
      return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
  },

  mounted() {
    if (this.employeeId) this.fetchDashboard();
  },

  methods: {
    async fetchDashboard() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/dashboard');
        const d = data.data || {};
        this.counts              = d.counts               || { leave: 0, loans: 0, payslips: 0, attendance: 0 };
        this.leaveBalances       = d.leave_balances       || [];
        this.recentLeave         = d.recent_leave         || [];
        this.upcomingShifts      = d.upcoming_shifts      || [];
        this.latestPayslip       = d.latest_payslip       || null;
        this.attendanceSummary   = d.attendance_summary   || { present: 0, absent: 0, late: 0, on_leave: 0 };
        this.unreadNotifications = d.unread_notifications || 0;
        this.recentNotifications = d.recent_notifications || [];
        this.upcomingBirthdays   = d.upcoming_birthdays   || [];
        this.announcements       = d.announcements        || [];
        this.recentDocuments     = d.recent_documents     || [];
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load dashboard');
      } finally {
        this.loading = false;
      }
    },

    barWidth(b) {
      if (!b.entitled || b.entitled <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((b.available / b.entitled) * 100)));
    },

    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    fmtTime(t) {
      if (!t) return '—';
      const [h, m] = t.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },

    fmtCurrency(val) {
      if (val == null || val === '') return '—';
      return Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    dayOfWeek(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'short' });
    },

    statusClass(s) {
      const map = { approved: 'pill--green', rejected: 'pill--red', pending: 'pill--orange', cancelled: 'pill--gray' };
      return map[s] || 'pill--gray';
    },

    stripHtml(html) {
      return (html || '').replace(/<[^>]*>/g, '').trim();
    },
    notifIcon(type) { return (NOTIF_META[type] || NOTIF_META.default).icon; },
    notifIconClass(type) { return (NOTIF_META[type] || NOTIF_META.default).cls; },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },
    timeAgo,
    docMiniIcon(mime) {
      if (!mime) return 'file-alt';
      if (mime.includes('pdf')) return 'file-pdf';
      if (mime.includes('word') || mime.includes('document')) return 'file-word';
      if (mime.includes('excel') || mime.includes('sheet')) return 'file-excel';
      if (mime.includes('image')) return 'file-image';
      return 'file-alt';
    },
  },
};
</script>

<style scoped>
/* ── Page layout ──────────────────────────────────────────── */
.page-header   { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title    { font-size:22px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub      { font-size:13px; color:#7a90b0; margin:0; }
.header-date   { font-size:12px; color:#94a3b8; font-weight:500; padding-top:4px; }

/* ── No profile ───────────────────────────────────────────── */
.no-profile-box   { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:16px; }
.no-profile-icon  { font-size:48px; color:#dde5f0; }
.no-profile-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.no-profile-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:360px; }

/* ── Stat cards ───────────────────────────────────────────── */
.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.stat-card {
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-icon-wrap {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.stat-body { min-width: 0; }
.stat-count { font-size: 26px; font-weight: 800; color: #1a2b45; margin: 0 0 2px; line-height: 1; }
.stat-label { font-size: 11px; color: #7a90b0; margin: 0; font-weight: 500; }

.sk-stat {
  height: 82px;
  background: linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%);
  background-size: 200% 100%; animation: shimmer 1.4s infinite;
}

/* ── Grid ─────────────────────────────────────────────────── */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* ── Widget card ──────────────────────────────────────────── */
.widget {
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.widget-head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.widget-head > div:nth-child(2) { flex: 1; min-width: 0; }
.widget-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
.widget-sub   { font-size: 11px; color: #94a3b8; margin: 0; }
.widget-link  { font-size: 12px; color: #004AAD; font-weight: 600; text-decoration: none; white-space: nowrap; }
.widget-link:hover { text-decoration: underline; }
.widget-empty { font-size: 13px; color: #b0bec5; text-align: center; padding: 20px 0; }

/* ── Icon wraps ───────────────────────────────────────────── */
.widget-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}
.icon--blue   { background: #dbeafe; color: #1d4ed8; }
.icon--green  { background: #dcfce7; color: #16a34a; }
.icon--orange { background: #ffedd5; color: #ea580c; }
.icon--red    { background: #fee2e2; color: #dc2626; }
.icon--purple { background: #ede9fe; color: #7c3aed; }
.icon--teal   { background: #ccfbf1; color: #0d9488; }
.icon--gray   { background: #f1f5f9; color: #64748b; }
.icon--pink   { background: #fce7f3; color: #be185d; }
.icon--indigo { background: #e0e7ff; color: #4338ca; }

/* ── Skeleton ─────────────────────────────────────────────── */
.sk-widget { gap: 12px; }
.sk-title {
  height: 16px; width: 50%; border-radius: 4px;
  background: linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%);
  background-size: 200% 100%; animation: shimmer 1.4s infinite;
}
.sk-body {
  height: 80px; border-radius: 8px;
  background: linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%);
  background-size: 200% 100%; animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Leave balances ───────────────────────────────────────── */
.leave-balance-list { display: flex; flex-direction: column; gap: 10px; }
.leave-balance-row  { display: flex; align-items: center; gap: 10px; }
.lb-name     { font-size: 12px; color: #1a2b45; width: 100px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lb-bar-wrap { flex: 1; height: 6px; background: #e4eaf2; border-radius: 99px; overflow: hidden; }
.lb-bar      { height: 100%; background: #004AAD; border-radius: 99px; transition: width .4s; }
.lb-count    { font-size: 11px; color: #64748b; white-space: nowrap; width: 80px; text-align: right; }

/* ── Recent leave requests ────────────────────────────────── */
.leave-request-list { display: flex; flex-direction: column; gap: 8px; }
.leave-req-row      { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 10px; background: #f8fafc; border-radius: 8px; }
.lr-info   { flex: 1; min-width: 0; }
.lr-type   { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0 0 2px; }
.lr-dates  { font-size: 11px; color: #7a90b0; margin: 0; }

/* Status pills */
.status-pill { font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 20px; text-transform: capitalize; white-space: nowrap; }
.pill--green  { background: #dcfce7; color: #16a34a; }
.pill--red    { background: #fee2e2; color: #dc2626; }
.pill--orange { background: #ffedd5; color: #ea580c; }
.pill--gray   { background: #f1f5f9; color: #64748b; }

/* ── Upcoming shifts ──────────────────────────────────────── */
.shift-list { display: flex; flex-direction: column; gap: 8px; }
.shift-row  { display: flex; align-items: center; gap: 14px; padding: 10px 12px; background: #f8fafc; border-radius: 8px; }
.shift-date-col { flex-shrink: 0; text-align: center; }
.shift-dow  { font-size: 10px; font-weight: 700; color: #004AAD; text-transform: uppercase; margin: 0; }
.shift-date { font-size: 11px; color: #7a90b0; margin: 0; }
.shift-info { flex: 1; min-width: 0; }
.shift-name { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shift-time { font-size: 11px; color: #7a90b0; margin: 0; }

/* ── Payslip ──────────────────────────────────────────────── */
.payslip-body { display: flex; flex-direction: column; gap: 10px; }
.pay-row      { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #f8fafc; border-radius: 8px; }
.pay-row--net { background: #f0f7ff; border: 1px solid #d0e7ff; }
.pay-label    { font-size: 12px; color: #7a90b0; }
.pay-value    { font-size: 13px; font-weight: 600; color: #1a2b45; }
.pay-value--net { font-size: 16px; color: #004AAD; }
.pay-value--sm  { font-size: 12px; font-weight: 500; }

/* ── Attendance ───────────────────────────────────────────── */
.attendance-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.att-card { border-radius: 10px; padding: 14px 12px; text-align: center; }
.att-card--present { background: #dcfce7; }
.att-card--absent  { background: #fee2e2; }
.att-card--late    { background: #ffedd5; }
.att-card--leave   { background: #dbeafe; }
.att-count { font-size: 26px; font-weight: 800; color: #1a2b45; margin: 0 0 4px; line-height: 1; }
.att-label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; margin: 0; }

/* ── Notifications mini ───────────────────────────────────── */
.unread-badge { background: #fee2e2; color: #dc2626; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }

.notif-mini-list { display: flex; flex-direction: column; gap: 6px; }
.notif-mini-row  { display: flex; align-items: flex-start; gap: 10px; padding: 10px; background: #f8fafc; border-radius: 8px; cursor: default; }
.notif-mini-row--unread { background: #f0f7ff; }

.notif-mini-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
.notif-mini-body { flex: 1; min-width: 0; }
.notif-mini-msg  { font-size: 12px; color: #1a2b45; margin: 0 0 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.notif-mini-time { font-size: 10px; color: #94a3b8; margin: 0; }

.notif-dot { width: 8px; height: 8px; border-radius: 50%; background: #004AAD; flex-shrink: 0; margin-top: 4px; }

/* ── Birthdays ────────────────────────────────────────────── */
.birthday-list { display: flex; flex-direction: column; gap: 8px; }
.birthday-row  { display: flex; align-items: center; gap: 12px; padding: 8px 10px; background: #fdf2f8; border-radius: 8px; }

.bday-avatar {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #f472b6, #be185d);
  color: #fff; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.bday-info  { flex: 1; min-width: 0; }
.bday-name  { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bday-date  { font-size: 11px; color: #7a90b0; margin: 0; }

.bday-today-pill { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 20px; background: #fce7f3; color: #be185d; white-space: nowrap; }
.bday-days       { font-size: 11px; font-weight: 600; color: #be185d; white-space: nowrap; }

/* ── Announcements ────────────────────────────────────────── */
.widget--full { grid-column: 1 / -1; }

.announcement-list { display: flex; flex-direction: column; gap: 12px; }
.announcement-item { padding: 14px 16px; background: #f8f9ff; border: 1px solid #e0e7ff; border-left: 4px solid #4338ca; border-radius: 8px; }

.ann-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.ann-title  { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0; }
.ann-date   { font-size: 11px; color: #94a3b8; white-space: nowrap; flex-shrink: 0; }
.ann-body   { font-size: 13px; color: #475569; margin: 0; line-height: 1.55; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

.ann-footer         { display: flex; align-items: center; gap: 12px; margin-top: 10px; }
.ann-stat           { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #7a90b0; }
.stat-icon          { font-size: 11px; color: #7a90b0; }
.stat-icon--red     { color: #e03535; }
.ann-interact-link  { margin-left: auto; font-size: 12px; font-weight: 600; color: #004AAD; text-decoration: none; }
.ann-interact-link:hover { text-decoration: underline; }

.widget-view-all { font-size: 12px; font-weight: 600; color: #004AAD; text-decoration: none; white-space: nowrap; }
.widget-view-all:hover { text-decoration: underline; }

/* ── Recent documents ─────────────────────────────────────── */
.doc-mini-list { display: flex; flex-direction: column; gap: 6px; }
.doc-mini-row  { display: flex; align-items: center; gap: 10px; padding: 9px 10px; background: #f8fafc; border-radius: 8px; cursor: default; }
.doc-mini-row--unread { background: #f0f7ff; }
.doc-mini-icon { font-size: 16px; color: #004AAD; flex-shrink: 0; width: 20px; text-align: center; }
.doc-mini-body { flex: 1; min-width: 0; }
.doc-mini-title { font-size: 12px; font-weight: 600; color: #1a2b45; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.doc-mini-meta  { font-size: 10px; color: #94a3b8; margin: 0; }
.doc-mini-badges { display: flex; gap: 4px; flex-shrink: 0; }
.mini-badge      { font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 20px; }
.mini-badge--blue  { background: #dbeafe; color: #1d4ed8; }
.mini-badge--amber { background: #fef9c3; color: #ca8a04; }
</style>
